import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, Download, Facebook, Loader2, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { FORMATS, dataUrlToBlob, drawShareCard } from "@/lib/shareCard";
import { facebookShareUrl, shareCaption, sharedUrl } from "@/lib/share";

const btn =
  "inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[0.68rem] uppercase tracking-[0.18em] transition-colors duration-400";

/** Condivisione social di un articolo, pensata prima di tutto per lo smartphone. */
export const ShareArticle = ({ article, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState("story");
  const [preview, setPreview] = useState("");
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const photoRef = useRef(null);

  const openWith = (next) => {
    setFormat(next);
    setOpen(true);
  };

  const build = useCallback(() => {
    setBusy(true);
    try {
      const canvas = drawShareCard(article, format, photoRef.current);
      // data URL: generazione immediata, niente URL temporanei da liberare.
      const url = canvas.toDataURL("image/jpeg", 0.9);
      setPreview(url);
      setBlob(dataUrlToBlob(url));
    } catch (err) {
      console.error("share-card", err);
      toast.error("Non siamo riusciti a preparare la grafica. Riprova.");
    } finally {
      setBusy(false);
    }
  }, [article, format]);

  useEffect(() => {
    if (open) build();
  }, [open, build]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const filename = `osteria-aterrana-${article.slug}-${format}.jpg`;
  const caption = shareCaption(article, "share");

  const nativeShare = async () => {
    if (!blob) return;
    const file = new File([blob], filename, { type: "image/jpeg" });
    const payload = { files: [file], title: article.title, text: caption };
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share(payload);
        trackEvent("article_share", { article_slug: article.slug, format, platform: "native" });
      } catch (err) {
        if (err?.name !== "AbortError") toast.error("Condivisione annullata dal dispositivo.");
      }
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, text: caption, url: sharedUrl(article.slug, "share") });
        trackEvent("article_share", { article_slug: article.slug, format, platform: "native" });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    download();
  };

  const download = () => {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = preview;
    a.download = filename;
    a.click();
    trackEvent("article_share", { article_slug: article.slug, format, platform: "download" });
    toast.success("Immagine salvata: ora puoi caricarla su Instagram o Facebook.");
  };

  const copy = async (text, what) => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      // Safari e i contesti senza permesso: ripiego sulla selezione manuale.
      const area = document.createElement("textarea");
      area.value = text;
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      try {
        ok = document.execCommand("copy");
      } catch {
        ok = false;
      }
      area.remove();
    }
    if (!ok) {
      toast.error("Il browser non ha permesso la copia: seleziona il testo qui sotto.");
      return;
    }
    setCopied(what);
    setTimeout(() => setCopied(""), 2000);
    trackEvent("article_share", { article_slug: article.slug, format, platform: "copy_link" });
  };

  const shareFacebook = () => {
    window.open(facebookShareUrl(article.slug), "_blank", "noopener,width=620,height=640");
    trackEvent("article_share", { article_slug: article.slug, format, platform: "facebook" });
  };

  const canNative = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <>
      {/* Copia della foto pronta all'uso: quando si apre la finestra la grafica esce subito. */}
      {!compact && article.hero_image && (
        <img
          ref={photoRef}
          src={`${mediaUrl(article.hero_image)}?w=1200`}
          alt=""
          aria-hidden="true"
          style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        />
      )}
      {compact ? (
        <button
          type="button"
          onClick={() => openWith("story")}
          data-testid="share-open-compact"
          className="inline-flex items-center gap-2 text-xs tracking-wider text-sage hover:text-wood transition-colors duration-300"
        >
          <Share2 size={14} strokeWidth={1.5} /> Condividi
        </button>
      ) : (
        <div className="mt-16 border-t border-wood/15 pt-10" data-testid="share-article">
          <p className="eyebrow text-gold">Condividi questo articolo</p>
          <p className="mt-3 text-sm text-foreground/65">
            Prepariamo noi la grafica: tu scegli il formato e la condividi dove preferisci.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" onClick={() => openWith("story")} data-testid="share-story"
              className={`${btn} bg-sage text-cream hover:bg-wood`}>
              <Share2 size={14} strokeWidth={1.5} /> Condividi nelle Storie
            </button>
            <button type="button" onClick={() => openWith("post")} data-testid="share-post"
              className={`${btn} border border-wood/30 text-wood hover:bg-wood hover:text-cream`}>
              Condividi come Post
            </button>
          </div>
        </div>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-wood-deep/80 p-4 py-10 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Condividi l'articolo"
          data-testid="share-dialog"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <div className="w-full max-w-md bg-cream p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow text-gold">Dal Diario</p>
                <h2 className="mt-2 font-serif text-2xl leading-snug text-wood">Condividi questo articolo</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Chiudi" data-testid="share-close"
                className="text-wood/50 hover:text-wood transition-colors duration-300">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="mt-5 flex gap-2" role="group" aria-label="Formato">
              {Object.entries(FORMATS).map(([key, f]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFormat(key)}
                  data-testid={`share-format-${key}`}
                  className={`flex-1 border px-3 py-2.5 text-[0.62rem] uppercase tracking-[0.14em] transition-colors duration-300 ${
                    format === key ? "border-sage bg-sage text-cream" : "border-wood/25 text-wood/70 hover:border-wood"
                  }`}
                >
                  {f.label} {f.ratio}
                </button>
              ))}
            </div>

            <div className="mt-5 flex justify-center bg-beige/40 p-4" data-testid="share-preview">
              {busy && <p className="py-16 text-sm text-wood/60"><Loader2 className="inline animate-spin" size={15} /> Prepariamo la grafica…</p>}
              {!busy && preview && (
                <img
                  src={preview}
                  alt={`Anteprima ${FORMATS[format].label} dell'articolo ${article.title}`}
                  data-testid="share-preview-image"
                  className="max-h-[46vh] w-auto shadow-lg"
                />
              )}
            </div>

            <div className="mt-5 space-y-2.5">
              <button type="button" onClick={nativeShare} disabled={!blob} data-testid="share-native"
                className={`${btn} w-full bg-wood text-cream hover:bg-sage disabled:opacity-50`}>
                <Share2 size={14} strokeWidth={1.5} /> Condividi
              </button>
              <p className="text-center text-xs leading-relaxed text-foreground/55">
                {canNative
                  ? "Si apre il menu del telefono: scegli Instagram, Facebook o l'app che preferisci."
                  : "Da computer scarica l'immagine e caricala dall'app, oppure condividi il link qui sotto."}
              </p>

              <div className="grid grid-cols-2 gap-2.5 pt-1">
                <button type="button" onClick={download} disabled={!preview} data-testid="share-download"
                  className={`${btn} border border-wood/30 text-wood hover:bg-wood hover:text-cream disabled:opacity-50`}>
                  <Download size={14} strokeWidth={1.5} /> Salva
                </button>
                <button type="button" onClick={shareFacebook} data-testid="share-facebook"
                  className={`${btn} border border-wood/30 text-wood hover:bg-wood hover:text-cream`}>
                  <Facebook size={14} strokeWidth={1.5} /> Facebook
                </button>
                <button type="button" onClick={() => copy(caption, "caption")} data-testid="share-copy-caption"
                  className={`${btn} border border-wood/30 text-wood hover:bg-wood hover:text-cream`}>
                  {copied === "caption" ? <Check size={14} /> : <Copy size={14} strokeWidth={1.5} />} Didascalia
                </button>
                <button type="button" onClick={() => copy(sharedUrl(article.slug, "share"), "link")} data-testid="share-copy-link"
                  className={`${btn} border border-wood/30 text-wood hover:bg-wood hover:text-cream`}>
                  {copied === "link" ? <Check size={14} /> : <Copy size={14} strokeWidth={1.5} />} Link
                </button>
              </div>

              <pre className="mt-3 max-h-28 overflow-y-auto whitespace-pre-wrap border border-wood/15 bg-beige/30 p-3 text-xs leading-relaxed text-foreground/70" data-testid="share-caption-text">
{caption}
              </pre>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareArticle;
